import { useState, useCallback, useRef, useMemo, useEffect } from "react";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const COLORS = ["black", "violet", "red", "green", "blue"];

const COLOR_STYLES = {
  black:  { bg: "#1a1a2e", border: "#3a3a5a", label: "Black",  emoji: "⬛" },
  violet: { bg: "#7c3aed", border: "#9d65f5", label: "Violet", emoji: "🟣" },
  red:    { bg: "#c0392b", border: "#e05a4a", label: "Red",    emoji: "🟥" },
  green:  { bg: "#1a7a40", border: "#27ae60", label: "Green",  emoji: "🟩" },
  blue:   { bg: "#1a5fa8", border: "#2980b9", label: "Blue",   emoji: "🟦" },
};

const GRID_SIZE      = 4;
const STARTING_SCORE = 99999;
const COST_MULT      = 100;

// ─── MODE DEFINITIONS ─────────────────────────────────────────────────────────
const MODES = {
  standard: {
    id:       "standard",
    label:    "STANDARD",
    icon:     "⚖️",
    tagline:  "Time + Turns",
    desc:     "Moderate penalties on both the clock and your move count. The balanced challenge.",
    turnMult: 1,
    timePenaltyPerSec: (elapsed) => elapsed < 60 ? 15 : 50,
    tierDefs: [
      { label: "1–10",  cost: "−100",   costNum: 100  },
      { label: "11–25", cost: "−200",   costNum: 200  },
      { label: "26–50", cost: "−400",   costNum: 400  },
      { label: "51+",   cost: "−1,000", costNum: 1000 },
    ],
  },
  time: {
    id:       "time",
    label:    "TIME PRESSURE",
    icon:     "⏱",
    tagline:  "Clock Only",
    desc:     "No turn penalty — take as many moves as you need. But the clock bleeds you dry.",
    turnMult: 0,
    timePenaltyPerSec: (elapsed) => elapsed < 60 ? 50 : 150,
    tierDefs: [
      { label: "0–60s", cost: "−50/s",  costNum: 50  },
      { label: "60s+",  cost: "−150/s", costNum: 150 },
    ],
  },
  efficiency: {
    id:       "efficiency",
    label:    "EFFICIENCY",
    icon:     "🎯",
    tagline:  "Turns Only",
    desc:     "No time pressure — the clock is your friend. But every move costs double. Plan carefully.",
    turnMult: 2,
    timePenaltyPerSec: () => 0,
    tierDefs: [
      { label: "1–10",  cost: "−200",   costNum: 200  },
      { label: "11–25", cost: "−400",   costNum: 400  },
      { label: "26–50", cost: "−800",   costNum: 800  },
      { label: "51+",   cost: "−2,000", costNum: 2000 },
    ],
  },
};

// ─── THEMES ───────────────────────────────────────────────────────────────────
const THEME = {
  dark: {
    pageBg:      "#09090f",
    panelBg:     "#0e0e1c",
    panelBorder: "#22223a",
    overlayBg:   "rgba(6,6,15,0.96)",
    titleColor:  "#eeeef8",
    accentColor: "#7c3aed",
    bodyText:    "#b0b0cc",
    dimText:     "#666688",
    mutedText:   "#444460",
    hintText:    "#333350",
    winSubColor: "#888899",
    bgGradient:
      "radial-gradient(ellipse at 20% 10%, rgba(124,58,237,0.07) 0%, transparent 50%)," +
      "radial-gradient(ellipse at 80% 90%, rgba(192,57,43,0.07) 0%, transparent 50%)",
    toggleIcon:  "☀️",
    toggleTip:   "Switch to light mode",
    codeBg:      "#050510",
  },
  light: {
    pageBg:      "#f3f1ed",
    panelBg:     "#ffffff",
    panelBorder: "#ddd8d0",
    overlayBg:   "rgba(240,237,232,0.97)",
    titleColor:  "#18182a",
    accentColor: "#6d28d9",
    bodyText:    "#3a3a52",
    dimText:     "#7070a0",
    mutedText:   "#a0a0c0",
    hintText:    "#c0c0d8",
    winSubColor: "#7070a0",
    bgGradient:
      "radial-gradient(ellipse at 20% 10%, rgba(124,58,237,0.04) 0%, transparent 50%)," +
      "radial-gradient(ellipse at 80% 90%, rgba(192,57,43,0.04) 0%, transparent 50%)",
    toggleIcon:  "🌙",
    toggleTip:   "Switch to dark mode",
    codeBg:      "#f0eeea",
  },
};

// ─── SCORING ──────────────────────────────────────────────────────────────────
function turnCost(n, mult) {
  if (!mult) return 0;
  let base;
  if (n <= 10)      base = 1  * COST_MULT;
  else if (n <= 25) base = 2  * COST_MULT;
  else if (n <= 50) base = 4  * COST_MULT;
  else              base = 10 * COST_MULT;
  return base * mult;
}

function getActiveTurnTier(turnsTaken) {
  if (turnsTaken + 1 > 50) return 3;
  if (turnsTaken + 1 > 25) return 2;
  if (turnsTaken + 1 > 10) return 1;
  return 0;
}

function formatTime(s) {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

// ─── DAILY SEED ───────────────────────────────────────────────────────────────
function getDailySeed() {
  const d = new Date();
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}

function formatSeedDate(seed) {
  const s = String(seed);
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[parseInt(s.slice(4,6)) - 1]} ${parseInt(s.slice(6,8))}, ${s.slice(0,4)}`;
}

function getPuzzleNumber(seed) {
  const toJD = (s) => {
    const n = String(s);
    const y = parseInt(n.slice(0,4)), m = parseInt(n.slice(4,6)), d = parseInt(n.slice(6,8));
    return Math.floor(365.25*(y+4716)) + Math.floor(30.6001*(m+1)) + d - 1524;
  };
  return Math.max(1, toJD(seed) - toJD(20260228) + 1);
}

// ─── SEEDED RNG ───────────────────────────────────────────────────────────────
function makeRng(seed) {
  let s = seed >>> 0;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 0x100000000;
  };
}

// ─── DAILY PALETTE ────────────────────────────────────────────────────────────
function getDailyPalette(seed) {
  const rand = makeRng((seed * 999983 + 31) >>> 0);
  return Array.from({length: 5}, () => COLORS[Math.floor(rand() * COLORS.length)]);
}

// ─── SHARE TEXT ───────────────────────────────────────────────────────────────
function buildShareText(score, turns, timeElapsed, seed, gameMode, evilMode) {
  const palette = getDailyPalette(seed);
  const squares = palette.map(c => COLOR_STYLES[c].emoji).join("");
  const modeTag = MODES[gameMode].tagline + (evilMode ? " ⚡ EVIL" : "");
  const timeHasValue = MODES[gameMode].timePenaltyPerSec(0) > 0;
  return [
    `Chrom-Addict ${formatSeedDate(seed)}`,
    `[${modeTag}]`,
    `${squares} ${score.toLocaleString()}`,
    `${turns} turn${turns !== 1 ? "s" : ""}${timeHasValue ? ` · ${formatTime(timeElapsed)}` : ""}`,
    `chrom-addict.com`,
  ].join("\n");
}

// ─── RULE ENGINE ──────────────────────────────────────────────────────────────
function allTargets(rule) {
  if (rule.type === "simple")      return new Set([rule.target]);
  if (rule.type === "alternating") return new Set(rule.targets);
  if (rule.type === "sequential")  return new Set(rule.targets);
  return new Set();
}

function hasDeadEndLoop(rules) {
  const reach = {};
  for (const c of COLORS) reach[c] = allTargets(rules[c]);
  for (const a of COLORS) for (const b of COLORS) {
    if (a === b || !reach[a].has(b) || !reach[b].has(a)) continue;
    const ae = [...reach[a]].some(c => c !== a && c !== b);
    const be = [...reach[b]].some(c => c !== a && c !== b);
    if (!ae && !be) return true;
  }
  return false;
}

function hasSelfLoop(rules) {
  return COLORS.some(c => allTargets(rules[c]).has(c));
}

function generateRuleset(seed) {
  const rand      = makeRng(seed);
  const pick      = arr        => arr[Math.floor(rand() * arr.length)];
  const pickEx    = (arr, ex)  => pick(arr.filter(x => x !== ex));
  const pickExAll = (arr, exs) => pick(arr.filter(x => !exs.includes(x)));

  for (let attempt = 0; attempt < 300; attempt++) {
    const rules = {}, descs = {};
    for (const color of COLORS) {
      const type = pick(["simple","simple","alternating","sequential"]);
      if (type === "simple") {
        const t = pickEx(COLORS, color);
        rules[color] = { type:"simple", target:t };
        descs[color] = `Always → ${t}`;
      } else if (type === "alternating") {
        const a = pickEx(COLORS, color);
        const b = pickExAll(COLORS, [color, a]);
        rules[color] = { type:"alternating", targets:[a,b] };
        descs[color] = `Odd clicks → ${a}, even → ${b}`;
      } else {
        const pool = [...COLORS.filter(c => c !== color)];
        for (let i = pool.length-1; i>0; i--) {
          const j = Math.floor(rand()*(i+1));
          [pool[i],pool[j]] = [pool[j],pool[i]];
        }
        const seq = pool.slice(0,3);
        rules[color] = { type:"sequential", targets:seq };
        descs[color] = `Cycles: ${seq.join(" → ")} → repeat`;
      }
    }
    if (!hasSelfLoop(rules) && !hasDeadEndLoop(rules))
      return { rules, ruleDescriptions: descs };
  }
  const rules = {}, ruleDescriptions = {};
  const ft = {black:"violet",violet:"red",red:"green",green:"blue",blue:"black"};
  for (const c of COLORS) {
    rules[c] = { type:"simple", target:ft[c] };
    ruleDescriptions[c] = `Always → ${ft[c]}`;
  }
  return { rules, ruleDescriptions };
}

function applyRule(rule, n) {
  if (rule.type === "simple")      return rule.target;
  if (rule.type === "alternating") return rule.targets[n % 2];
  if (rule.type === "sequential")  return rule.targets[n % rule.targets.length];
  return rule.target;
}

// ─── GRID ─────────────────────────────────────────────────────────────────────
function generateGrid(seed) {
  const rand = makeRng((seed * 31337 + 7) >>> 0);
  return Array.from({length:GRID_SIZE}, () =>
    Array.from({length:GRID_SIZE}, () => COLORS[Math.floor(rand()*COLORS.length)])
  );
}

function checkWin(grid) {
  const f = grid[0][0];
  return grid.every(r => r.every(c => c === f));
}

// ─── SCORE DIGIT TILES ────────────────────────────────────────────────────────
function ScoreDigits({ score, palette, large }) {
  const str      = String(score).padStart(5,"0");
  const size     = large ? 52 : 40;
  const fontSize = large ? "1.5rem" : "1.15rem";
  return (
    <div style={{display:"flex", gap:4}}>
      {str.split("").map((d,i) => {
        const cs = COLOR_STYLES[palette[i]];
        return (
          <div key={i} style={{
            width:size, height:Math.round(size*1.2),
            background:cs.bg, border:`1px solid ${cs.border}`,
            borderRadius:6,
            display:"flex", alignItems:"center", justifyContent:"center",
          }}>
            <span style={{
              fontSize, fontWeight:900, color:"rgba(255,255,255,0.95)",
              fontVariantNumeric:"tabular-nums", lineHeight:1,
              textShadow:"0 1px 6px rgba(0,0,0,0.7)",
            }}>
              {d}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── MODE SELECT ──────────────────────────────────────────────────────────────
function ModeSelect({ theme: themeName, seed, onStart }) {
  const [selected, setSelected]   = useState("standard");
  const [evilMode, setEvilMode]   = useState(false);
  const [howToPlay, setHowToPlay] = useState(false);
  const t = THEME[themeName];

  return (
    <div style={{
      position:"fixed", inset:0,
      background: themeName === "dark" ? "rgba(5,5,12,0.97)" : "rgba(235,231,226,0.97)",
      display:"flex", alignItems:"center", justifyContent:"center",
      zIndex:300, backdropFilter:"blur(4px)",
    }}>
      <div style={{
        width:"100%", maxWidth:480,
        padding:"28px 20px 32px",
        display:"flex", flexDirection:"column", gap:16,
      }}>
        {/* Header */}
        <div style={{textAlign:"center", marginBottom:4}}>
          <div style={{display:"flex", alignItems:"baseline", justifyContent:"center", gap:0}}>
            <span style={{fontSize:"2rem", fontWeight:900, letterSpacing:"0.05em", color:t.titleColor}}>CHROM</span>
            <span style={{fontSize:"2rem", fontWeight:900, letterSpacing:"0.05em", color:t.accentColor}}>-ADDICT</span>
          </div>
          <div style={{fontSize:"0.78rem", color:t.dimText, letterSpacing:"0.18em", marginTop:4, fontFamily:"'Courier New',monospace"}}>
            SELECT YOUR MODE
          </div>
        </div>

        {/* How to Play — collapsible */}
        <div style={{
          border:`1px solid ${t.panelBorder}`,
          borderRadius:10, overflow:"hidden",
        }}>
          <button
            onClick={() => setHowToPlay(h => !h)}
            style={{
              width:"100%", background:"transparent", border:"none",
              padding:"12px 16px", cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"space-between",
            }}
          >
            <span style={{
              fontSize:"0.72rem", fontWeight:800, letterSpacing:"0.16em",
              color:t.dimText, fontFamily:"'Courier New',monospace",
            }}>
              HOW TO PLAY
            </span>
            <span style={{
              fontSize:"0.75rem", color:t.dimText,
              transform: howToPlay ? "rotate(180deg)" : "rotate(0deg)",
              transition:"transform 0.2s",
              display:"inline-block",
            }}>
              ▾
            </span>
          </button>

          {howToPlay && (
            <div style={{
              padding:"4px 16px 14px",
              borderTop:`1px solid ${t.panelBorder}`,
              display:"flex", flexDirection:"column", gap:8,
            }}>
              {[
                ["Click any cell", "It changes color based on a hidden rule for that color. The board starts mixed — your job is to get every cell to the same color."],
                ["Same color, same rule", "Every cell of a given color follows the same rule. Click any blue cell and you'll always get the same result — use that to map the board."],
                ["Rules can alternate or cycle", "Some colors swap between two targets on odd vs. even clicks. Others rotate through a fixed sequence of three colors. Watch the pattern."],
                ["Your score depends on your mode", "Standard penalizes both turns and time. Time Pressure ignores turns but bleeds points every second. Efficiency doubles the turn cost but stops the clock entirely."],
                ["Evil Mode", "Every 10 turns, the hidden rules change — but the board stays exactly as you left it. The countdown at the top tells you how many turns you have left before the rules shift."],
              ].map(([bold, rest], i) => (
                <div key={i} style={{display:"flex", gap:10, alignItems:"flex-start"}}>
                  <span style={{
                    fontSize:"0.75rem", color:t.accentColor, fontWeight:700,
                    marginTop:2, flexShrink:0,
                  }}>→</span>
                  <span style={{fontSize:"0.85rem", color:t.bodyText, lineHeight:1.45}}>
                    <strong style={{color:t.titleColor}}>{bold}</strong> — {rest}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Mode cards */}
        <div style={{display:"flex", flexDirection:"column", gap:8}}>
          {Object.values(MODES).map(mode => {
            const isSelected = selected === mode.id;
            return (
              <button
                key={mode.id}
                onClick={() => setSelected(mode.id)}
                style={{
                  background: isSelected
                    ? (themeName === "dark" ? "rgba(124,58,237,0.18)" : "rgba(109,40,217,0.10)")
                    : t.panelBg,
                  border: isSelected
                    ? `2px solid ${t.accentColor}`
                    : `1px solid ${t.panelBorder}`,
                  borderRadius:10,
                  padding:"14px 16px",
                  cursor:"pointer",
                  textAlign:"left",
                  display:"flex", alignItems:"center", gap:14,
                  transition:"all 0.15s",
                  boxShadow: isSelected
                    ? `0 0 0 3px ${themeName === "dark" ? "rgba(124,58,237,0.15)" : "rgba(109,40,217,0.10)"}`
                    : "none",
                }}
              >
                <span style={{fontSize:"1.6rem", lineHeight:1, flexShrink:0}}>{mode.icon}</span>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{
                    fontSize:"0.72rem", fontWeight:800,
                    letterSpacing:"0.16em", color: isSelected ? t.accentColor : t.dimText,
                    fontFamily:"'Courier New',monospace",
                  }}>
                    {mode.label}
                    <span style={{
                      marginLeft:8, fontSize:"0.68rem",
                      color: isSelected ? t.accentColor : t.mutedText,
                      fontWeight:600,
                    }}>
                      · {mode.tagline}
                    </span>
                  </div>
                  <div style={{
                    fontSize:"0.85rem", color:t.bodyText, marginTop:3, lineHeight:1.4,
                  }}>
                    {mode.desc}
                  </div>
                </div>
                {isSelected && (
                  <div style={{
                    width:18, height:18, borderRadius:"50%",
                    background:t.accentColor, flexShrink:0,
                    display:"flex", alignItems:"center", justifyContent:"center",
                  }}>
                    <span style={{color:"#fff", fontSize:"0.65rem", fontWeight:900}}>✓</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Evil mode toggle */}
        <button
          onClick={() => setEvilMode(e => !e)}
          style={{
            background: evilMode
              ? (themeName === "dark" ? "rgba(180,30,30,0.20)" : "rgba(160,20,20,0.10)")
              : t.panelBg,
            border: evilMode ? "2px solid #c0392b" : `1px solid ${t.panelBorder}`,
            borderRadius:10, padding:"14px 16px",
            cursor:"pointer", textAlign:"left",
            display:"flex", alignItems:"center", gap:14,
            transition:"all 0.15s",
            boxShadow: evilMode ? "0 0 0 3px rgba(192,57,43,0.15)" : "none",
          }}
        >
          <span style={{fontSize:"1.6rem", lineHeight:1, flexShrink:0}}>⚡</span>
          <div style={{flex:1, minWidth:0}}>
            <div style={{
              fontSize:"0.72rem", fontWeight:800, letterSpacing:"0.16em",
              color: evilMode ? "#e05a4a" : t.dimText,
              fontFamily:"'Courier New',monospace",
            }}>
              EVIL MODE
              <span style={{
                marginLeft:8, fontSize:"0.68rem", fontWeight:600,
                color: evilMode ? "#e05a4a" : t.mutedText,
              }}>
                · Stacks on top of selected mode
              </span>
            </div>
            <div style={{fontSize:"0.85rem", color:t.bodyText, marginTop:3, lineHeight:1.4}}>
              Every 10 turns, the hidden rules change. The board stays the same. Good luck.
            </div>
          </div>
          <div style={{
            width:32, height:18, borderRadius:9, flexShrink:0,
            background: evilMode ? "#c0392b" : t.panelBorder,
            position:"relative", transition:"background 0.2s",
          }}>
            <div style={{
              position:"absolute", top:2,
              left: evilMode ? 14 : 2,
              width:14, height:14, borderRadius:"50%",
              background:"#fff",
              transition:"left 0.2s",
              boxShadow:"0 1px 4px rgba(0,0,0,0.3)",
            }} />
          </div>
        </button>

        {/* Start button */}
        <button
          onClick={() => onStart(selected, evilMode)}
          style={{
            background:t.accentColor, border:"none", color:"#fff",
            padding:"16px", fontSize:"0.95rem", fontWeight:800,
            letterSpacing:"0.14em", cursor:"pointer", borderRadius:8,
            fontFamily:"'Courier New',monospace",
            marginTop:4,
          }}
        >
          {evilMode ? "⚡ START EVIL RUN" : "▶ START PUZZLE"}
        </button>
      </div>
    </div>
  );
}

// ─── SHARE MODAL ──────────────────────────────────────────────────────────────
function ShareModal({ score, turns, timeElapsed, seed, theme, gameMode, evilMode, onClose }) {
  const [copied, setCopied] = useState(false);
  const t       = THEME[theme];
  const palette = getDailyPalette(seed);
  const text    = buildShareText(score, turns, timeElapsed, seed, gameMode, evilMode);

  const copy = async () => {
    try { await navigator.clipboard.writeText(text); } catch {}
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  return (
    <div
      style={{
        position:"fixed", inset:0,
        background: theme === "dark" ? "rgba(0,0,0,0.85)" : "rgba(60,50,40,0.6)",
        display:"flex", alignItems:"center", justifyContent:"center",
        zIndex:200, backdropFilter:"blur(8px)",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background:t.panelBg, border:`1px solid ${t.panelBorder}`,
          borderRadius:16, padding:"32px 28px",
          display:"flex", flexDirection:"column", alignItems:"center", gap:20,
          maxWidth:360, width:"92%",
          boxShadow: theme === "dark" ? "0 32px 80px rgba(0,0,0,0.7)" : "0 16px 48px rgba(0,0,0,0.18)",
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{fontSize:"0.75rem", letterSpacing:"0.2em", color:t.dimText, fontFamily:"'Courier New',monospace"}}>
          SHARE YOUR SCORE
        </div>

        <ScoreDigits score={score} palette={palette} large />

        <pre style={{
          background:t.codeBg, border:`1px solid ${t.panelBorder}`,
          borderRadius:10, padding:"16px 20px",
          fontSize:"1rem", lineHeight:1.9,
          color:t.titleColor,
          fontFamily:"'Courier New',monospace",
          margin:0, textAlign:"center", whiteSpace:"pre-wrap",
          width:"100%", boxSizing:"border-box",
        }}>
          {text}
        </pre>

        <div style={{display:"flex", gap:10, width:"100%"}}>
          <button onClick={copy} style={{
            flex:2, background:t.accentColor, border:"none", color:"#fff",
            padding:"13px 0", fontSize:"0.85rem", fontWeight:700,
            letterSpacing:"0.12em", cursor:"pointer", borderRadius:6,
            fontFamily:"'Courier New',monospace",
          }}>
            {copied ? "✓  COPIED!" : "COPY TO CLIPBOARD"}
          </button>
          <button onClick={onClose} style={{
            flex:1, background:"transparent",
            border:`1px solid ${t.panelBorder}`,
            color:t.dimText, padding:"13px 0",
            fontSize:"0.82rem", cursor:"pointer", borderRadius:6,
            fontFamily:"'Courier New',monospace",
          }}>
            CLOSE
          </button>
        </div>

        <p style={{fontSize:"0.78rem", color:t.dimText, textAlign:"center", margin:0, lineHeight:1.5}}>
          Paste anywhere to challenge friends
        </p>
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function ChromAddict() {
  const dailySeed    = getDailySeed();
  const puzzleNum    = getPuzzleNumber(dailySeed);
  const dateLabel    = formatSeedDate(dailySeed);
  const dailyPalette = getDailyPalette(dailySeed);

  // ── Game phase & mode ──
  // phases: "checking" | "selecting" | "playing" | "won" | "already-played"
  const [gamePhase,      setGamePhase]      = useState("checking");
  const [gameMode,       setGameMode]       = useState("standard");
  const [evilModeOn,     setEvilModeOn]     = useState(false);
  const [evilGeneration, setEvilGeneration] = useState(0);

  // ── Core game state ──
  const [grid,        setGrid]        = useState(() => generateGrid(dailySeed));
  const [colorClicks, setColorClicks] = useState({});
  const [turnsTaken,  setTurnsTaken]  = useState(0);
  const [score,       setScore]       = useState(STARTING_SCORE);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [lastFlash,   setLastFlash]   = useState(null);
  const [showShare,   setShowShare]   = useState(false);
  const [theme,       setTheme]       = useState("dark");

  // ── Refs ──
  const flashRef       = useRef(null);
  const timerRef       = useRef(null);
  const startTimeRef   = useRef(null);
  const gameModeRef    = useRef("standard");

  // ── Derive rules from seed (evil mode shifts seed by −10 years per generation) ──
  const ruleSeed = evilModeOn
    ? (dailySeed - evilGeneration * 100000)
    : dailySeed;

  const { rules } = useMemo(
    () => generateRuleset(ruleSeed),
    [ruleSeed]
  );

  const t = THEME[theme];

  // ── Check if already played today ──
  useEffect(() => {
    async function checkPlayed() {
      try {
        const res = await fetch(`/api/check-played?puzzleDate=${dailySeed}`);
        const data = await res.json();
        setGamePhase(data.played ? "already-played" : "selecting");
      } catch {
        // If the check fails, fail open and let them play.
        setGamePhase("selecting");
      }
    }
    checkPlayed();
  }, [dailySeed]);

  // ── Clean up timer on unmount ──
  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  // ── Mode start ──
  const handleStart = useCallback((mode, evil) => {
    setGameMode(mode);
    setEvilModeOn(evil);
    gameModeRef.current = mode;
    setGamePhase("playing");
  }, []);

  // ── Cell click ──
  const handleClick = useCallback((row, col) => {
    if (gamePhase !== "playing") return;

    // Start timer on first click
    if (!startTimeRef.current) {
      startTimeRef.current = Date.now();
      timerRef.current = setInterval(() => {
        if (!startTimeRef.current) return;
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setTimeElapsed(elapsed);
        const penalty = MODES[gameModeRef.current].timePenaltyPerSec(elapsed);
        if (penalty > 0) setScore(prev => Math.max(0, prev - penalty));
      }, 1000);
    }

    const cur     = grid[row][col];
    const n       = colorClicks[cur] || 0;
    const next    = applyRule(rules[cur], n);
    const newGrid = grid.map((r,ri) => r.map((c,ci) => ri===row && ci===col ? next : c));
    const newTurns = turnsTaken + 1;
    const cost     = turnCost(newTurns, MODES[gameModeRef.current].turnMult);
    const newScore = Math.max(0, score - cost);

    // Evil mode: rule change every 10 turns — colorClicks reset, generation increments
    let newClicks = { ...colorClicks, [cur]: n + 1 };
    let newEvilGen = evilGeneration;
    if (evilModeOn && newTurns % 10 === 0) {
      newEvilGen = evilGeneration + 1;
      newClicks  = {}; // reset click counters so new rules start fresh
    }

    setGrid(newGrid);
    setColorClicks(newClicks);
    setTurnsTaken(newTurns);
    setScore(newScore);
    if (newEvilGen !== evilGeneration) setEvilGeneration(newEvilGen);

    // Flash
    if (flashRef.current) clearTimeout(flashRef.current);
    setLastFlash(`${row}-${col}`);
    flashRef.current = setTimeout(() => setLastFlash(null), 220);

    if (checkWin(newGrid)) {
      setGamePhase("won");
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      logGameResult({
        puzzleDate:   String(dailySeed),
        puzzleNumber: puzzleNum,
        turnsTaken:   newTurns,
        finalScore:   newScore,
        timeElapsed:  Math.floor((Date.now() - (startTimeRef.current || Date.now())) / 1000),
        solved:       true,
        mode:         gameModeRef.current,
        evilMode:     evilModeOn,
      });
    }
  }, [grid, rules, colorClicks, turnsTaken, score, gamePhase, evilModeOn, evilGeneration, dailySeed, puzzleNum]);

  // ── Derived display values ──
  const currentMode     = MODES[gameMode];
  const hasTimePenalty  = currentMode.timePenaltyPerSec(0) > 0;
  const hasTurnPenalty  = currentMode.turnMult > 0;
  const nextTurnCost    = turnCost(turnsTaken + 1, currentMode.turnMult);
  const activeTurnTier  = getActiveTurnTier(turnsTaken);
  const activeTimeTier  = timeElapsed >= 60 ? 1 : 0;
  const activeTier      = gameMode === "time" ? activeTimeTier : activeTurnTier;
  const turnsUntilReset = 10 - (turnsTaken % 10);

  const nextCostColor =
    activeTurnTier >= 3 ? "#ff4444" :
    activeTurnTier >= 2 ? "#ff8844" :
    activeTurnTier >= 1 ? "#ffcc44" : "#55dd88";

  const evilCountdownColor =
    turnsUntilReset <= 2 ? "#ff4444" :
    turnsUntilReset <= 4 ? "#ff8844" :
    turnsUntilReset <= 6 ? "#ffcc44" : "#55dd88";

  return (
    <div style={{
      minHeight:"100vh",
      background:t.pageBg,
      fontFamily:"system-ui, -apple-system, 'Segoe UI', sans-serif",
      display:"flex", alignItems:"flex-start", justifyContent:"center",
      padding:"24px 16px 56px",
      position:"relative",
      transition:"background 0.3s",
    }}>
      {/* Ambient glow */}
      <div style={{position:"fixed", inset:0, pointerEvents:"none", backgroundImage:t.bgGradient}} />

      {/* Mode select overlay */}
      {/* Checking overlay — shown briefly while we verify play status */}
      {gamePhase === "checking" && (
        <div style={{
          position:"fixed", inset:0,
          background: theme === "dark" ? "rgba(5,5,12,0.97)" : "rgba(235,231,226,0.97)",
          display:"flex", alignItems:"center", justifyContent:"center",
          zIndex:300,
        }}>
          <div style={{textAlign:"center"}}>
            <div style={{fontSize:"2rem", marginBottom:12}}>⧗</div>
            <div style={{
              fontSize:"0.75rem", letterSpacing:"0.2em",
              color:t.dimText, fontFamily:"'Courier New',monospace",
            }}>
              LOADING PUZZLE...
            </div>
          </div>
        </div>
      )}

      {/* Already played overlay */}
      {gamePhase === "already-played" && (
        <div style={{
          position:"fixed", inset:0,
          background: theme === "dark" ? "rgba(5,5,12,0.97)" : "rgba(235,231,226,0.97)",
          display:"flex", alignItems:"center", justifyContent:"center",
          zIndex:300,
        }}>
          <div style={{
            width:"100%", maxWidth:400, padding:"0 24px",
            display:"flex", flexDirection:"column", alignItems:"center", gap:16,
            textAlign:"center",
          }}>
            <div style={{fontSize:"2.5rem"}}>🔒</div>
            <div style={{
              fontSize:"1.6rem", fontWeight:900, letterSpacing:"0.08em",
              color:t.titleColor,
            }}>
              ALREADY PLAYED
            </div>
            <div style={{fontSize:"0.95rem", color:t.bodyText, lineHeight:1.6}}>
              You've already completed today's puzzle.
              A new one drops at midnight.
            </div>
            <div style={{
              fontSize:"0.8rem", color:t.dimText,
              fontFamily:"'Courier New',monospace", letterSpacing:"0.1em",
              marginTop:4,
            }}>
              #{puzzleNum} · {dateLabel}
            </div>
          </div>
        </div>
      )}

      {/* Mode select overlay */}
      {gamePhase === "selecting" && (
        <ModeSelect theme={theme} seed={dailySeed} onStart={handleStart} />
      )}

      {/* Share modal */}
      {showShare && (
        <ShareModal
          score={score} turns={turnsTaken} timeElapsed={timeElapsed}
          seed={dailySeed} theme={theme} gameMode={gameMode} evilMode={evilModeOn}
          onClose={() => setShowShare(false)}
        />
      )}

      <div style={{width:"100%", maxWidth:480, position:"relative", zIndex:1}}>

        {/* ── Header ──────────────────────────────────────────── */}
        <div style={{display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:8}}>
          <div>
            <div style={{display:"flex", alignItems:"baseline", lineHeight:1}}>
              <span style={{fontSize:"2.2rem", fontWeight:900, letterSpacing:"0.05em", color:t.titleColor}}>
                CHROM
              </span>
              <span style={{fontSize:"2.2rem", fontWeight:900, letterSpacing:"0.05em", color:t.accentColor}}>
                -ADDICT
              </span>
            </div>
            <div style={{display:"flex", gap:12, marginTop:5, alignItems:"center", flexWrap:"wrap"}}>
              <span style={{fontSize:"0.85rem", color:t.accentColor, fontWeight:700, letterSpacing:"0.06em"}}>
                #{puzzleNum}
              </span>
              <span style={{fontSize:"0.85rem", color:t.dimText}}>
                {dateLabel}
              </span>
              {gamePhase !== "selecting" && (
                <span style={{
                  fontSize:"0.72rem", fontWeight:700, letterSpacing:"0.12em",
                  color: evilModeOn ? "#e05a4a" : t.dimText,
                  fontFamily:"'Courier New',monospace",
                  background: evilModeOn
                    ? (theme === "dark" ? "rgba(192,57,43,0.15)" : "rgba(192,57,43,0.10)")
                    : (theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"),
                  padding:"2px 7px", borderRadius:4,
                  border: evilModeOn ? "1px solid rgba(192,57,43,0.4)" : `1px solid ${t.panelBorder}`,
                }}>
                  {evilModeOn ? "⚡ EVIL · " : ""}{currentMode.label}
                </span>
              )}
            </div>
          </div>

          {/* Theme toggle */}
          <button
            title={t.toggleTip}
            onClick={() => setTheme(th => th === "dark" ? "light" : "dark")}
            style={{
              background:"transparent", border:`1px solid ${t.panelBorder}`,
              borderRadius:8, padding:"8px 12px", fontSize:"1.1rem",
              cursor:"pointer", lineHeight:1, marginTop:4,
            }}
          >
            {t.toggleIcon}
          </button>
        </div>

        <p style={{fontSize:"0.95rem", color:t.bodyText, margin:"0 0 18px", lineHeight:1.5}}>
          Make the entire board one color. Each color follows a hidden rule — figure them out as you play. Your score depends on the mode you chose.
        </p>

        {/* ── Score bar ───────────────────────────────────────── */}
        <div style={{
          background:t.panelBg, border:`1px solid ${t.panelBorder}`,
          borderRadius:12, padding:"16px 18px", marginBottom:16,
          transition:"background 0.3s",
        }}>
          {/* Top row */}
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:10, flexWrap:"wrap", gap:4}}>
            <span style={{fontSize:"0.75rem", color:t.dimText, letterSpacing:"0.12em", fontWeight:600}}>
              TODAY'S SCORE
            </span>
            <span style={{fontSize:"0.82rem", color:t.bodyText}}>
              <span style={{fontWeight:700, color:t.titleColor}}>{turnsTaken}</span>
              {" turns"}
              {hasTurnPenalty && nextTurnCost > 0 && (
                <> · next <span style={{fontWeight:700, color:nextCostColor}}>−{nextTurnCost.toLocaleString()}</span></>
              )}
              {hasTimePenalty && startTimeRef.current && (
                <> · <span style={{fontWeight:700, color:t.titleColor}}>⏱ {formatTime(timeElapsed)}</span></>
              )}
            </span>
          </div>

          {/* Score digits */}
          <div style={{marginBottom:14}}>
            <ScoreDigits score={score} palette={dailyPalette} />
          </div>

          {/* Cost tier row */}
          <div style={{display:"flex", gap:6}}>
            {currentMode.tierDefs.map((td, i) => (
              <div key={i} style={{
                flex:1, borderRadius:6, padding:"6px 4px", textAlign:"center",
                border: i === activeTier ? `2px solid ${t.accentColor}` : `1px solid ${t.panelBorder}`,
                background: i === activeTier
                  ? (theme === "dark" ? "rgba(124,58,237,0.15)" : "rgba(109,40,217,0.08)")
                  : "transparent",
                opacity: i === activeTier ? 1 : 0.45,
                transition:"all 0.2s",
              }}>
                <div style={{fontSize:"0.8rem", fontWeight:700, color:t.titleColor}}>{td.cost}</div>
                <div style={{fontSize:"0.72rem", color:t.dimText, marginTop:2}}>{td.label}</div>
              </div>
            ))}
          </div>

          {/* Standard mode: time penalty note */}
          {gameMode === "standard" && (
            <div style={{
              marginTop:8, fontSize:"0.72rem", color:t.dimText,
              fontFamily:"'Courier New',monospace",
            }}>
              + time drain: −15/s (0–60s) → −50/s (60s+)
            </div>
          )}
        </div>

        {/* ── Evil Mode countdown ──────────────────────────────── */}
        {evilModeOn && gamePhase === "playing" && (
          <div style={{
            background: theme === "dark" ? "rgba(192,57,43,0.10)" : "rgba(192,57,43,0.07)",
            border:`1px solid rgba(192,57,43,0.35)`,
            borderRadius:10, padding:"10px 16px", marginBottom:12,
            display:"flex", alignItems:"center", justifyContent:"space-between",
          }}>
            <div style={{display:"flex", alignItems:"center", gap:8}}>
              <span style={{fontSize:"1rem"}}>⚡</span>
              <span style={{
                fontSize:"0.75rem", fontWeight:700, letterSpacing:"0.12em",
                color:"#e05a4a", fontFamily:"'Courier New',monospace",
              }}>
                EVIL MODE
              </span>
              <span style={{fontSize:"0.82rem", color:t.bodyText}}>
                · rules change in
              </span>
            </div>
            <div style={{
              fontSize:"1.4rem", fontWeight:900,
              color: evilCountdownColor,
              fontFamily:"'Courier New',monospace",
              fontVariantNumeric:"tabular-nums",
              minWidth:32, textAlign:"right",
              transition:"color 0.3s",
            }}>
              {turnsUntilReset}
            </div>
          </div>
        )}

        {/* ── Grid ────────────────────────────────────────────── */}
        <div style={{position:"relative", marginBottom:16}}>
          {/* Win overlay */}
          {gamePhase === "won" && (
            <div style={{
              position:"absolute", inset:0,
              background:t.overlayBg,
              borderRadius:12, zIndex:10,
              display:"flex", alignItems:"center", justifyContent:"center",
              backdropFilter:"blur(12px)",
            }}>
              <div style={{
                textAlign:"center", display:"flex", flexDirection:"column",
                alignItems:"center", gap:14, padding:"0 16px",
              }}>
                <div style={{fontSize:"2.5rem"}}>🎉</div>
                <div style={{fontSize:"1.8rem", fontWeight:900, letterSpacing:"0.1em", color:t.titleColor}}>
                  SOLVED!
                </div>
                <ScoreDigits score={score} palette={dailyPalette} large />
                <div style={{fontSize:"1rem", color:t.winSubColor}}>
                  {turnsTaken} turn{turnsTaken !== 1 ? "s" : ""}
                  {hasTimePenalty && ` · ${formatTime(timeElapsed)}`}
                  {" · "}{dateLabel}
                </div>
                <button
                  onClick={() => setShowShare(true)}
                  style={{
                    background:t.accentColor, border:"none", color:"#fff",
                    padding:"14px 36px", fontSize:"1rem", fontWeight:700,
                    letterSpacing:"0.1em", cursor:"pointer", borderRadius:8,
                    marginTop:4,
                  }}
                >
                  📤 Share Result
                </button>
              </div>
            </div>
          )}

          {/* Game grid */}
          <div style={{
            display:"grid",
            gridTemplateColumns:`repeat(${GRID_SIZE},1fr)`,
            gap:8, padding:14,
            background:t.panelBg, border:`1px solid ${t.panelBorder}`,
            borderRadius:12, transition:"background 0.3s",
          }}>
            {grid.map((row, ri) => row.map((color, ci) => {
              const fl = lastFlash === `${ri}-${ci}`;
              const cs = COLOR_STYLES[color];
              return (
                <button
                  key={`${ri}-${ci}`}
                  onClick={() => handleClick(ri, ci)}
                  disabled={gamePhase !== "playing"}
                  style={{
                    aspectRatio:"1", borderRadius:8, cursor:"pointer",
                    background:cs.bg,
                    border:`2px solid ${fl ? "rgba(255,255,255,0.8)" : cs.border}`,
                    boxShadow: fl
                      ? `0 0 0 3px white, 0 0 22px ${cs.bg}`
                      : "inset 0 1px 0 rgba(255,255,255,0.1), 0 3px 10px rgba(0,0,0,0.4)",
                    transform: fl ? "scale(0.88)" : "scale(1)",
                    transition:"transform 0.1s ease, box-shadow 0.12s ease, border-color 0.1s ease",
                    opacity: gamePhase === "selecting" ? 0.4 : 1,
                  }}
                />
              );
            }))}
          </div>
        </div>

        {/* ── How to play ─────────────────────────────────────── */}
        <div style={{
          background:t.panelBg, border:`1px solid ${t.panelBorder}`,
          borderRadius:12, padding:"14px 18px", marginBottom:16,
          transition:"background 0.3s",
        }}>
          <div style={{fontSize:"0.78rem", fontWeight:700, color:t.dimText, letterSpacing:"0.1em", marginBottom:10}}>
            HOW TO PLAY
          </div>
          <div style={{display:"flex", flexDirection:"column", gap:7}}>
            {[
              ["Click any cell", "It changes to a new color based on a hidden rule. Every cell of the same color follows the same rule — use that to build your map."],
              ["Rules can alternate or cycle", "Some colors swap targets on odd vs. even clicks. Others rotate through a fixed sequence of three. Click a few times to figure out the pattern."],
              ["Score based on your mode", "Standard drains points per turn and per second. Time Pressure only charges by the clock. Efficiency only charges by the move — at double cost."],
              ["Make the whole board one color", "Once every cell matches, the puzzle is solved. The fewer penalties you accumulate, the higher your final score."],
            ].map(([bold, rest], i) => (
              <div key={i} style={{display:"flex", gap:10, alignItems:"flex-start"}}>
                <span style={{
                  fontSize:"0.75rem", color:t.accentColor, fontWeight:700,
                  marginTop:1, flexShrink:0,
                }}>→</span>
                <span style={{fontSize:"0.9rem", color:t.bodyText, lineHeight:1.45}}>
                  <strong style={{color:t.titleColor}}>{bold}</strong> — {rest}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Footer ──────────────────────────────────────────── */}
        {gamePhase === "won" && (
          <button
            onClick={() => setShowShare(true)}
            style={{
              width:"100%", background:t.accentColor, border:"none", color:"#fff",
              padding:"14px", fontSize:"1rem", fontWeight:700,
              letterSpacing:"0.1em", cursor:"pointer", borderRadius:8,
              marginBottom:16,
            }}
          >
            📤 Share Your Score
          </button>
        )}

        <p style={{fontSize:"0.82rem", color:t.mutedText, textAlign:"center", lineHeight:1.5, margin:0}}>
          A new puzzle every day · chrom-addict.com
        </p>

      </div>
    </div>
  );
}

// ─── ANALYTICS ────────────────────────────────────────────────────────────────
async function logGameResult({ puzzleDate, puzzleNumber, turnsTaken, finalScore, timeElapsed, solved, mode, evilMode }) {
  try {
    await fetch("/api/log-game", {
      method:"POST",
      headers:{"Content-Type":"application/json"},
      body:JSON.stringify({ puzzleDate, puzzleNumber, turnsTaken, finalScore, timeElapsed, solved, mode, evilMode }),
    });
  } catch {
    // Never interrupt gameplay for analytics
  }
}
